import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap } from 'rxjs/operators';

import { ChatHttpService } from '@core/http/chat.http.service';
import { parseError } from '@state/errors.parser';

import { UserAssetsHttpService } from '@core/http/user-assets.http.service';
import { SingleFileUploadResult, UserAsset } from '@interfaces';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import * as chatActions from './chat.actions';
import { FileHandlerService } from '@core/services/file-handler.service';

type ChatUploadResult = {
  errorMessage: any;
  uploaded: boolean;
  userAsset: UserAsset;
};

@Injectable()
export class ChatEffects {
  private readonly actions$ = inject(Actions);
  private readonly chatHttp = inject(ChatHttpService);
  private readonly assetsHttp = inject(UserAssetsHttpService);
  private readonly stackedMessages = inject(StackedMessagesService);
  private readonly fileHandlerService = inject(FileHandlerService);

  getMyChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.getMyChat),
      switchMap(() => {
        return this.chatHttp.getMyChat().pipe(
          map(({ messages, supportChat }) => {
            return chatActions.getMyChatSuccess({ messages, supportChat });
          }),
          catchError(error => {
            console.error(`Error 'getMyChat'`);
            console.error(error);
            this.stackedMessages.showError({ message: 'NT3.ERROR_DOWNLOADING_CHAT_DATA' });

            const errorMessage = parseError(error, chatActions.getMyChat.type);
            return of(chatActions.getMyChatError({ errorMessage }));
          })
        );
      })
    )
  );

  addChatMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.addChatMessage),
      switchMap(({ opts }) => {
        return this.chatHttp.addChatMessage(opts).pipe(
          map(message => {
            return chatActions.addChatMessageSuccess({ message });
          }),
          catchError(error => {
            console.error(`Error 'addChatMessage'`);
            console.error(error);
            this.stackedMessages.showError({ message: 'COMMON.ERROR.SENDING_MESSAGE' });

            const errorMessage = parseError(error, chatActions.addChatMessage.type);
            return of(chatActions.addChatMessageError({ errorMessage }));
          })
        );
      })
    )
  );

  uploadAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.uploadAttachments),
      concatMap(({ opts }) => {
        const { files } = opts;
        const resultsObj: { [key: string]: Observable<SingleFileUploadResult> } = files.reduce(
          (acc, currFile) => {
            acc[currFile.fileOriginalName] = this.assetsHttp.uploadMessageAttachment({ file: currFile.file }).pipe(
              map((uploadResult): ChatUploadResult => {
                return { errorMessage: null, uploaded: true, userAsset: uploadResult };
              }),
              catchError(error => {
                console.error(`Error 'uploadAttachments'`);
                console.error(error);
                this.stackedMessages.showError({
                  message: 'COMMON.ERROR.ERROR_UPLOADING_FILE',
                  translateParams: {
                    fileName: currFile.fileOriginalName,
                  },
                });
                const errorMessage = parseError(error, chatActions.uploadAttachments.type);
                return of<ChatUploadResult>({
                  errorMessage,
                  uploaded: false,
                  userAsset: { fileOriginalName: currFile.fileOriginalName } as UserAsset,
                });
              })
            );
            return acc;
          },
          {} as { [key: string]: Observable<SingleFileUploadResult> }
        );

        return forkJoin(resultsObj).pipe(
          map(uploadedFiles => {
            return chatActions.uploadAttachmentsSuccess({ opts: { uploadedFiles } });
          }),
          catchError(err => {
            console.error(`Error 'uploadAttachments'`);
            console.error(err);
            const errorMessage = parseError(err, chatActions.uploadAttachments.type);
            return of(chatActions.uploadAttachmentsError({ opts: { errorMessage } }));
          })
        );
      })
    )
  );

  downloadAttachmentFromChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.downloadAttachmentFromChat),
      mergeMap(({ messageId, attachmentId, previewTargetKey }) => {
        return this.chatHttp.downloadAttachmentFromChat(messageId, attachmentId).pipe(
          map(response => {
            if (
              this.fileHandlerService.getFile(response, {
                tryOpenOnMobile: true,
                previewTargetKey,
                allowDownloadFallback: true,
              })
            ) {
              return chatActions.downloadAttachmentFromChatSuccess();
            } else {
              return chatActions.downloadAttachmentFromChatError();
            }
          }),
          catchError(error => {
            console.error(`Error 'downloadAttachmentFromChat'`);
            console.error(error);
            if (previewTargetKey) {
              this.fileHandlerService.clearPreparedPreviewTarget(previewTargetKey);
            }
            this.stackedMessages.showError({ message: 'COMMON.ERROR.DOWNLOADING_ATTACHMENT' });
            return of(chatActions.downloadAttachmentFromChatError());
          })
        );
      })
    )
  );

  removeAttachmentFromUploading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.removeAttachmentFromUploading),
      switchMap(({ opts }) => {
        return this.assetsHttp.removeMyAsset(opts).pipe(
          map(() => {
            return chatActions.removeAttachmentFromUploadingSuccess({ opts });
          }),
          catchError(error => {
            console.error(`Error 'removeAttachmentFromUploading'`);
            console.error(error);
            this.stackedMessages.showError({ message: 'NT2.ERROR_REMOVING_FILE' });
            const errorMessage = parseError(error, chatActions.removeAttachmentFromUploading.type);
            return of(chatActions.removeAttachmentFromUploadingError({ opts: { ...opts, errorMessage } }));
          })
        );
      })
    )
  );
}
